import { isDefined } from '../../../../misc/functions.utilities'
import { BottomPanel } from '../BottomPanel'
import { Button } from '../../buttons'
import { FullLoader } from '../../loading'
import { Formik, FormikProps } from 'formik'
import styles from './ContactConfigureBottomPanel.module.scss'
import { FormikSelectInput } from '../../inputs'
import {
  CONTACT_GREETING_START_OPTIONS,
  CONTACT_GREETING_END_OPTIONS,
  ContactConfigureBottomPanelProps,
  ContactConfigureSchema,
} from './ContactConfigureBottomPanel.model'
import { ContactConfiguration } from '../../../../model/Contact'
import { useCallback, useEffect, useState } from 'react'
import { useGetContact, useUpdateContactConfiguration } from '../../../../hooks/queries/contact'
import { patientsKeys } from '../../../../hooks/queries/patients/patients.keys'
import { useQueryClient } from '@tanstack/react-query'

export const ContactConfigureBottomPanel = ({
  itemId,
  isUnmounting,
  patientId,
  onRequestClose,
  onItemEdited,
}: ContactConfigureBottomPanelProps) => {
  const queryClient = useQueryClient()

  const {
    query: { data: contact, isLoading },
  } = useGetContact({
    id: itemId ?? '',
  })

  const { mutate: updateContactConfiguration } = useUpdateContactConfiguration()

  const [contactConfiguration, setContactConfiguration] = useState<ContactConfiguration>(
    contact?.contactConfiguration ?? {
      startGreeting: null,
      endGreeting: null,
    },
  )

  const isButtonDisabled = !contactConfiguration.startGreeting || !contactConfiguration.endGreeting

  useEffect(() => {
    if (!contact?.contactConfiguration) {
      return
    }

    setContactConfiguration(contact?.contactConfiguration)
  }, [contact])

  const handleValidate = useCallback(() => {
    if (onItemEdited) {
      onItemEdited(contactConfiguration)
    }

    updateContactConfiguration(
      {
        id: itemId as string,
        payload: contactConfiguration,
      },
      {
        onSuccess: () => {
          if (patientId) {
            queryClient.invalidateQueries({ queryKey: patientsKeys.detail(patientId) })
          }
        },
      },
    )
  }, [
    contactConfiguration,
    itemId,
    onItemEdited,
    patientId,
    queryClient,
    updateContactConfiguration,
  ])

  const topBarActions = (
    <Button
      onClick={handleValidate}
      theme="primary"
      label="Valider"
      disabled={isButtonDisabled}
      testId="button-bottom_panel-submit"
      type="submit"
    />
  )
  return (
    <BottomPanel
      testId="contact-update"
      title="Éditer l'élément"
      isUnmounting={isUnmounting}
      onRequestClose={onRequestClose}
      actions={topBarActions}
    >
      {isDefined(itemId) && !isLoading ? (
        <Formik
          initialValues={contactConfiguration}
          validationSchema={ContactConfigureSchema}
          onSubmit={() => {}}
          enableReinitialize
          validateOnMount
        >
          {(props: FormikProps<ContactConfiguration>) => (
            <div
              onKeyPress={(e) => {
                if (e.key === 'Enter' && props.isValid) {
                  props.handleSubmit()
                }
              }}
            >
              <div className={styles.formContainer}>
                <FormikSelectInput
                  label="Politesse Début"
                  fieldName="startGreeting"
                  colorPreset="dark"
                  placeholder="Veuillez sélectionner une formule de politesse"
                  options={CONTACT_GREETING_START_OPTIONS}
                  value={CONTACT_GREETING_START_OPTIONS.find(
                    (option) => option.value === props.values.startGreeting,
                  )}
                  onChange={(selectedOption) => {
                    if (!selectedOption) {
                      return
                    }

                    setContactConfiguration({
                      ...contactConfiguration,
                      startGreeting: selectedOption,
                    })
                    props.setFieldValue('startGreeting', selectedOption)
                  }}
                  testId="contact-start_greeting"
                  required
                />
                <FormikSelectInput
                  label="Politesse Fin"
                  fieldName="endGreeting"
                  placeholder="Veuillez sélectionner une formule de politesse"
                  options={CONTACT_GREETING_END_OPTIONS}
                  colorPreset="dark"
                  value={CONTACT_GREETING_END_OPTIONS.find(
                    (option) => option.value === props.values.endGreeting,
                  )}
                  onChange={(selectedOption) => {
                    if (!selectedOption) {
                      return
                    }

                    setContactConfiguration({
                      ...contactConfiguration,
                      endGreeting: selectedOption,
                    })
                    props.setFieldValue('endGreeting', selectedOption)
                  }}
                  testId="contact-end_greeting"
                  required
                />
              </div>
            </div>
          )}
        </Formik>
      ) : (
        <FullLoader />
      )}
    </BottomPanel>
  )
}
